import { DownloadSimpleIcon, QrCodeIcon, RepeatIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetVisitorParkingQR } from '@/api/visitor-parking/get-visitor-parking-qr'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { BorderLoader } from '@/components/ui/loaders/border-loader'
import { Modal } from '@/components/ui/modal'
import { Typography } from '@/components/ui/typography'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { downloadHref } from '@/utils'

import * as S from './parking-qr-code-modal.styled'

export interface ParkingQrCodeModalProps {
  onClose: VoidFunction
}

export const ParkingQrCodeModal = ({ onClose }: ParkingQrCodeModalProps) => {
  const { t } = useTranslation(['parking-management'])
  const activeBuilding = useSelector(selectActiveBuilding)

  const { data, isLoading, isError, error, refetch } = useGetVisitorParkingQR({
    buildingId: activeBuilding?.id ?? 0,
  })

  const handleDownloadClick = () => {
    if (!data) return

    let fileName = 'visitor-parking-qr.png'
    const trimmed = activeBuilding?.name?.trim()
    if (trimmed) {
      fileName = `${trimmed}-` + fileName
    }
    downloadHref(data, fileName)
  }

  const handleRetry = () => refetch()

  return (
    <Modal
      title={t('parking-management:parkingQrCodeModal.title')}
      headerIcon={QrCodeIcon}
      footer={
        <>
          <Button use="secondary" onClick={onClose}>
            {t('parking-management:parkingQrCodeModal.cancel')}
          </Button>
          {!isError && (
            <Button disabled={isLoading} onClick={handleDownloadClick}>
              <Icon icon={DownloadSimpleIcon} />
              {t('parking-management:parkingQrCodeModal.download')}
            </Button>
          )}
          {isError && (
            <Button onClick={handleRetry}>
              <Icon icon={RepeatIcon} />
              {t('parking-management:parkingQrCodeModal.retry')}
            </Button>
          )}
        </>
      }
      onClose={onClose}
    >
      <S.DescriptionWrapper>
        <Typography color="black600" size="text-medium" weight="medium">
          {t('parking-management:parkingQrCodeModal.description')}
        </Typography>
      </S.DescriptionWrapper>
      {isLoading && <BorderLoader align="center" />}
      {!isLoading && isError && <Alert type="error" title={error.message} />}
      {!isLoading && !isError && (
        <S.QrCodeImage src={data} alt={t('parking-management:parkingQrCodeModal.imageAlt')} />
      )}
    </Modal>
  )
}
